import { AlternativePrice, Price, Shipping } from 'src/types';
import { getSortedShipping } from './shipping-sort';
import { FilterOffer, IFilter } from './types';
import { replaceMainOfferWithAlternativeOffer } from './utils';

const filterOffer = (offer: FilterOffer, filter: IFilter): boolean => {
  // Keep offers with missing info if we have a function defined for it
  return filter.missingInfoFn
    ? filter.filterFn(offer, filter.selection) || filter.missingInfoFn(offer)
    : filter.filterFn(offer, filter.selection);
};

function tryFindNewMainOffer(offer: Price, filter: IFilter): Price | null {
  const mainOfferIsValid = filter.filterFn(offer, filter.selection);
  if (mainOfferIsValid) return offer;

  const mainOfferHasMissingInfo =
    filter.missingInfoFn && filter.missingInfoFn(offer);

  if (!offer.alternativePrices?.length) {
    if (mainOfferHasMissingInfo) return offer;
    return null;
  }

  let firstValidAlternativeOffer = offer.alternativePrices.find(alt =>
    filter.filterFn(alt, filter.selection)
  );

  // If no valid alt offer but a main offer with missing info, return the main offer
  if (!firstValidAlternativeOffer && mainOfferHasMissingInfo) {
    return offer;
  }

  // If no valid alt offer, try finding one with missing info
  if (!firstValidAlternativeOffer && filter.missingInfoFn) {
    firstValidAlternativeOffer = offer.alternativePrices.find(alt =>
      filter.missingInfoFn!(alt)
    );
  }

  // If no offer (incl. missing info) is valid, return no offer
  if (!firstValidAlternativeOffer) {
    return null;
  }

  return replaceMainOfferWithAlternativeOffer(
    offer,
    firstValidAlternativeOffer
  );
}

export const filterOffersGeneric = (
  offers: Price[],
  filter: IFilter
): Price[] => {
  if (!offers.length || !filter.active) {
    return offers;
  }

  if (
    !filter.filterAlternativeOffers ||
    offers.every(offer => !offer.alternativePrices?.length)
  ) {
    return offers.filter(mainOffer => filterOffer(mainOffer, filter));
  }

  // Filtering alternative offers
  const filteredOffers: Price[] = [];
  offers.forEach(offer => {
    const filteredAlternatives = offer.alternativePrices?.filter(alt =>
      filterOffer(alt, filter)
    );
    if (!filteredAlternatives?.length) {
      return;
    }
    const offerWithFilteredAlternatives = {
      ...offer,
      alternativePrices: filteredAlternatives,
    };
    const newMainOffer = tryFindNewMainOffer(
      offerWithFilteredAlternatives,
      filter
    );
    if (newMainOffer) {
      filteredOffers.push(newMainOffer);
    }
  });
  return filteredOffers;
};

export const filterShippingsGeneric = (
  offers: Price[],
  filters: IFilter[]
): Price[] => {
  const applicapleFilters = filters.filter(
    filter => filter.active && filter.filterShippingFn
  );

  const filteredOffers = offers.map(
    offer => filterOfferShippings(offer, applicapleFilters) as Price
  );

  for (const offer of filteredOffers) {
    if (offer.alternativePrices) {
      offer.alternativePrices = offer.alternativePrices.map(
        altOffer =>
          filterOfferShippings(altOffer!, applicapleFilters) as AlternativePrice
      );
    }
  }

  return filteredOffers;
};

const filterOfferShippings = (
  offer: Price | AlternativePrice,
  filters: IFilter[]
): Price | AlternativePrice => {
  if (!offer.shipping || !offer.shipping.nodes) {
    return offer;
  }

  const shippingNodes = offer.shipping.nodes as Shipping[];

  const filteredShippings = shippingNodes.filter(shipping =>
    filters.every(filter =>
      filter.filterShippingFn!(shipping, filter.selection)
    )
  );
  const cheapest = getSortedShipping(filteredShippings, false);
  const fastest = getSortedShipping(filteredShippings, true);
  return {
    ...offer,
    shipping: { cheapest, fastest, nodes: filteredShippings },
  };
};
